import {
  SearchSettings,
  Price,
  SearchResponse,
  TourSearchResult,
  TourCardBadge
} from "@/types/search/tour-search-results";
import imageFactory from "@/factories/common/image-factory";
import { UNIFIED_SEARCH_CONFIG } from "@/config/unified-search-config";
import * as R from "ramda";
import { searchFiltersFactory } from "../search-filters/search-filters-factory";
import rangeFilterFactory from "../search-filters/range-filter-factory";

const priceFactory = (data: any): Price => ({
  value: data?.value ?? "",
  unit: data?.unit ?? ""
});

const tourStylesFactory = (styles: any[]) => styles
  .map((style: string) => (UNIFIED_SEARCH_CONFIG.fields.TourStyles.valueMap as Record<string, string>)[style])
  .filter((style) => style !== undefined);

// handles badge filtering and sorting

// any differences in locale must also be included in the enum so it is not filtered out
enum TourCardBadgesEnum {
  "Small Group",
  "Best Seller",
  "Travelers' Favorite",
  "Travellers' Favourite",
  "Limited Availability",
  "New Tour"
}

const isValidBadge = (label: string): label is keyof typeof TourCardBadgesEnum =>
  label in TourCardBadgesEnum;

const toBadge = (label: keyof typeof TourCardBadgesEnum): TourCardBadge => ({
  label,
  color: label === "Small Group" ? "primary" : "info"
});

const sortByEnumOrder = R.sort<TourCardBadge>((a, b) =>
  (TourCardBadgesEnum[a.label as keyof typeof TourCardBadgesEnum] as number) - 
  (TourCardBadgesEnum[b.label as keyof typeof TourCardBadgesEnum] as number)
);

export const tourCardBadgeFactory = (badges?: string[] | null, tourCallout?: string): TourCardBadge[] => {
  let amendedBadges: string[];

  if (Array.isArray(badges)) {
    if (tourCallout) {
      amendedBadges = [...badges, tourCallout];
    } else {
      amendedBadges = [...badges];
    }
  } else if (tourCallout) {
      amendedBadges = [tourCallout];
    } else {
      amendedBadges = [];
    }

  // Ensure amendedBadges is an array
  const badgesArray = Array.isArray(amendedBadges) ? amendedBadges.flat() : [];

  return R.pipe(
    R.filter(isValidBadge),
    R.map(toBadge),
    sortByEnumOrder
  )(badgesArray).slice(0,4);
};

const tourSearchResultFactory = (data: any): TourSearchResult => ({
  offerSavings: data?.offerSavings ?? undefined,
  searchImage: imageFactory({
    imageUrl: data?.searchImageUrl,
    altText: data?.searchImageAlt
  }),
  searchImageMap: imageFactory({
    imageUrl: data?.searchImageMapUrl,
    altText: data?.searchImageMapAlt
  }),
  highlights: data?.highlights ?? [],
  title: data?.title ?? "",
  subTitle: data?.subTitle ?? "",
  tourStyles: data?.tourStyles ? tourStylesFactory(data.tourStyles) : [],
  badges: tourCardBadgeFactory(data?.badges, data?.tourCallout),
  tourCallout: data?.tourCallout ?? "",
  crossedOutPrice: data?.crossedOutPrice ? priceFactory(data?.crossedOutPrice) : undefined,
  price: priceFactory(data?.price),
  feefoStars: data?.feefoStars ?? 0,
  feefoReviewCount: data?.feefoReviewCount ?? 0,
  features: data?.features ?? [],
  tourUrl: data?.tourUrl ?? "",
  bookingUrl: data?.bookingUrl ?? "",
  numberDays: data?.numberDays ?? "",
  numberMeals: data?.numberMeals ?? "",
  tourSlug: data?.tourSlug ?? "",
  tourId: data?.tourId ?? "",
  packageDateId: data?.packageDateId ?? "",
  webDescription: data?.webDescription ?? "",
  activityLevel: data?.activityLevel ? parseInt(data?.activityLevel, 10) : 0,
  activityLevelLabel: (UNIFIED_SEARCH_CONFIG.fields.ActivityLevel.valueMap as Record<string, string>)[data?.activityLevel] ?? "",
  comingSoon: data?.comingSoon ?? false,
  extensions: data?.extensions?.length > 0 ? data.extensions : undefined,
  tourDates: data?.tourDates ?? [],
  landingPageId: data?.landingPageId ?? ""
});

const searchSettingsFactory = (data: any): SearchSettings => ({
  currentPage: data?.currentPage ?? 1,
  pageCount: data?.pageCount ?? 1,
  pageSize: data?.pageSize ?? 0,
  totalCount: data?.totalCount ?? 0
});

const tourSearchResponseFactory = (data: any): SearchResponse => ({
  searchResults: data?.searchResults ? data.searchResults.map(tourSearchResultFactory) : [],
  searchRanges: data?.searchRanges ? data.searchRanges.map(rangeFilterFactory) : [],
  searchFilters: data?.searchFilters ? data.searchFilters.map(searchFiltersFactory) : [],
  searchSettings: searchSettingsFactory(data?.searchSettings),
  sortOptions: data?.sortOptions ?? []
});

export { tourSearchResponseFactory, tourSearchResultFactory };
